import { createSelector } from "@reduxjs/toolkit";
import { ReportField, ReportType } from "../../../shared/reporting/api/biClient.types";
import { getTabularFieldGridId } from "../../../shared/reporting/utils/getTabularFieldGridId.ts";
import {
  isChartReportType,
  isPivotReportType,
  isTabularReportType,
  isTileReportType,
} from "../../../shared/reporting/utils/reportTypeHelper.ts";
import { ChartFieldState, PivotFieldState, TabularFieldState, TileFieldState } from "../types/fieldsState.types";
import { RootState } from "../types/store.types";

export const selectTabularFieldsState = (state: RootState): TabularFieldState => {
  return state.fieldsState.tabularFieldsReducer;
};

export const selectTabularSettingsState = (state: RootState) => {
  return state.fieldsState.tabularFieldsReducer.settings;
};

export const selectPivotSettingsState = (state: RootState) => {
  return state.fieldsState.pivotFieldsReducer.settings;
};

export const selectChartSettingsState = (state: RootState) => {
  return state.fieldsState.chartFieldsReducer.settings;
};

export const selectTileSettingsState = (state: RootState) => {
  return state.fieldsState.tileFieldsReducer.settings;
};

export const selectPivotFieldsState = (state: RootState): PivotFieldState => {
  return state.fieldsState.pivotFieldsReducer;
};

export const selectChartFieldsState = (state: RootState): ChartFieldState => {
  return state.fieldsState.chartFieldsReducer;
};

export const selectTileFieldsState = (state: RootState): TileFieldState => {
  return state.fieldsState.tileFieldsReducer;
};

export const selectReportType = (state: RootState) => {
  return state.fieldsState.fieldsStateReducer.reportType;
};

export const selectTabularFields = (state: RootState) => {
  if (!isTabularReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.tabularFieldsReducer.fields;
};

export const selectTileFields = (state: RootState) => {
  if (!isTileReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.tileFieldsReducer.fields;
};

export const selectTabularGrouping = createSelector(
  (state: RootState) => state.fieldsState.fieldsStateReducer.reportType,
  (state: RootState) => state.fieldsState.tabularFieldsReducer.grouping,
  (reportType, grouping) => {
    if (reportType !== ReportType.Tabular) {
      throw new Error("Not supported");
    }
    return grouping;
  }
);

export const selectTabularFreezedColumns = createSelector(
  (state: RootState) => state.fieldsState.fieldsStateReducer.reportType,
  (state: RootState) => state.fieldsState.tabularFieldsReducer.settings.freezedColumns,
  (reportType, freezedColumns) => {
    if (reportType !== ReportType.Tabular) {
      throw new Error("Not supported");
    }
    return freezedColumns ?? [];
  }
);

export const selectFieldGridId = (state: RootState, field: ReportField) => {
  if (state.fieldsState.fieldsStateReducer.reportType !== ReportType.Tabular) {
    throw new Error("Not supported");
  }
  return getTabularFieldGridId(
    field,
    state.fieldsState.tabularFieldsReducer.grouping,
    state.fieldsState.tabularFieldsReducer.settings.showGroupedColumns
  );
};

export const selectIsFieldFreezed = (state: RootState, field: ReportField) => {
  if (state.fieldsState.fieldsStateReducer.reportType !== ReportType.Tabular) {
    throw new Error("Not supported");
  }
  return selectTabularFreezedColumns(state).includes(selectFieldGridId(state, field));
};

export const selectIsGroupedFields = (state: RootState, field: ReportField) => {
  if (state.fieldsState.fieldsStateReducer.reportType !== ReportType.Tabular) {
    throw new Error("Not supported");
  }
  return state.fieldsState.tabularFieldsReducer.grouping.some((g) => g.id === field.config.guid);
};

export const selectTabularShowGroupedColumns = (state: RootState) => {
  if (state.fieldsState.fieldsStateReducer.reportType !== ReportType.Tabular) {
    throw new Error("Not supported");
  }
  return state.fieldsState.tabularFieldsReducer.settings.showGroupedColumns;
};

export const selectSorts = (state: RootState) => {
  if (isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.pivotFieldsReducer.sorts;
  }

  if (isTabularReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.tabularFieldsReducer.sorts;
  }

  if (isChartReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.chartFieldsReducer.sorts;
  }

  throw new Error("Report type not supported");
};

export const selectConditions = (state: RootState) => {
  const reportType = state.fieldsState.fieldsStateReducer.reportType;
  if (!reportType) {
    throw new Error("Report type not initialized");
  }

  if (isPivotReportType(reportType)) {
    return state.fieldsState.pivotFieldsReducer.conditions;
  }

  if (isTabularReportType(reportType)) {
    return state.fieldsState.tabularFieldsReducer.conditions;
  }

  if (isChartReportType(reportType)) {
    return state.fieldsState.chartFieldsReducer.conditions;
  }

  if (isTileReportType(reportType)) {
    return state.fieldsState.tileFieldsReducer.conditions;
  }

  throw new Error("Report type not initialized");
};

export const selectValues = (state: RootState) => {
  if (
    isTabularReportType(state.fieldsState.fieldsStateReducer.reportType) ||
    isTileReportType(state.fieldsState.fieldsStateReducer.reportType)
  ) {
    throw new Error("Not supported");
  }

  if (isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.pivotFieldsReducer.values;
  }

  if (isChartReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    return state.fieldsState.chartFieldsReducer.values;
  }

  throw new Error("Report type not initialized");
};

export const selectRows = (state: RootState) => {
  if (!isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.pivotFieldsReducer.rows;
};

export const selectColumns = (state: RootState) => {
  if (!isPivotReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.pivotFieldsReducer.columns;
};

export const selectArguments = (state: RootState) => {
  if (!isChartReportType(state.fieldsState.fieldsStateReducer.reportType)) {
    throw new Error("Not supported");
  }
  return state.fieldsState.chartFieldsReducer.arguments;
};

export const selectFieldsStateConfiguration = createSelector(
  (state: RootState) => state.fieldsState.fieldsStateReducer.reportType,
  (state: RootState) => state.fieldsState.tabularFieldsReducer.configuration,
  (state: RootState) => state.fieldsState.pivotFieldsReducer.configuration,
  (state: RootState) => state.fieldsState.chartFieldsReducer.configuration,
  (state: RootState) => state.fieldsState.tileFieldsReducer.configuration,
  (reportType, tabularConfig, pivotConfig, chartConfig, tileConfig) => {
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (isTabularReportType(reportType)) {
      return tabularConfig;
    }
    if (isPivotReportType(reportType)) {
      return pivotConfig;
    }
    if (isChartReportType(reportType)) {
      return chartConfig;
    }
    if (isTileReportType(reportType)) {
      return tileConfig;
    }
    throw new Error("Report type not supported");
  }
);

export const selectFieldsStateIsConfigurationEmpty = (state: RootState) => {
  const reportType = state.fieldsState.fieldsStateReducer.reportType;
  if (!reportType) {
    return true;
  }
  if (isTabularReportType(reportType)) {
    return state.fieldsState.tabularFieldsReducer.isConfigurationEmpty;
  }
  if (isPivotReportType(reportType)) {
    return state.fieldsState.pivotFieldsReducer.isConfigurationEmpty;
  }
  if (isChartReportType(reportType)) {
    return state.fieldsState.chartFieldsReducer.isConfigurationEmpty;
  }
  if (isTileReportType(reportType)) {
    return state.fieldsState.tileFieldsReducer.isConfigurationEmpty;
  }
  throw new Error("Report type not supported");
};

const selectTabularDimensionNames = (state: RootState) => state.fieldsState.tabularFieldsReducer.dimensionNames;
const selectPivotDimensionNames = (state: RootState) => state.fieldsState.pivotFieldsReducer.dimensionNames;
const selectChartDimensionNames = (state: RootState) => state.fieldsState.chartFieldsReducer.dimensionNames;
const selectTabularMeasureNames = (state: RootState) => state.fieldsState.tabularFieldsReducer.measureNames;
const selectPivotMeasureNames = (state: RootState) => state.fieldsState.pivotFieldsReducer.measureNames;
const selectChartMeasureNames = (state: RootState) => state.fieldsState.chartFieldsReducer.measureNames;
const selectTileDimensionNames = (state: RootState) => state.fieldsState.tileFieldsReducer.dimensionNames;
const selectTileMeasureNames = (state: RootState) => state.fieldsState.tileFieldsReducer.measureNames;

export const selectSelectedDimensionNames = createSelector(
  [
    selectReportType,
    selectTabularDimensionNames,
    selectPivotDimensionNames,
    selectChartDimensionNames,
    selectTileDimensionNames,
  ],
  (reportType, tabularDimensions, pivotDimensions, chartDimensions, tileDimensions) => {
    if (!reportType) {
      return [];
    }
    if (isTabularReportType(reportType)) {
      return tabularDimensions;
    }
    if (isPivotReportType(reportType)) {
      return pivotDimensions;
    }
    if (isChartReportType(reportType)) {
      return chartDimensions;
    }
    if (isTileReportType(reportType)) {
      return tileDimensions;
    }
    throw new Error("Report type not supported");
  }
);

export const selectSelectedMeasureNames = createSelector(
  [
    selectReportType,
    selectTabularMeasureNames,
    selectPivotMeasureNames,
    selectChartMeasureNames,
    selectTileMeasureNames,
  ],
  (reportType, tabularMeasures, pivotMeasures, chartMeasures, tileMeasures) => {
    if (!reportType) {
      return [];
    }
    if (isTabularReportType(reportType)) {
      return tabularMeasures;
    }
    if (isPivotReportType(reportType)) {
      return pivotMeasures;
    }
    if (isChartReportType(reportType)) {
      return chartMeasures;
    }
    if (isTileReportType(reportType)) {
      return tileMeasures;
    }
    throw new Error("Report type not supported");
  }
);
